import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { authErrorMessage } from '../lib/auth-errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(authErrorMessage(data.detail, 'Unable to request a reset link.'));
      setSent(true);
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-light flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <Link to="/advertiser-login" className="inline-flex items-center gap-2 text-navy/70 hover:text-navy text-sm mb-5" data-testid="forgot-password-back">
          <ArrowLeft size={16} /> Back to login
        </Link>
        <h1 className="text-3xl font-bold text-navy mb-2" data-testid="forgot-password-title">Reset your password</h1>
        {!sent ? (
          <>
            <p className="text-navy/70 text-sm mb-6">
              Enter the email tied to your Advecti dashboard and we&apos;ll send you a one-time reset link.
            </p>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-navy/40" size={18} />
                <input
                  type="email"
                  aria-label="Email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  className="w-full pl-10 pr-3 py-3 rounded-lg border border-navy/15 focus:border-navy focus:outline-none"
                  data-testid="forgot-password-email-input"
                />
              </div>
              {error && <p role="alert" className="text-red-600 text-sm" data-testid="forgot-password-error">{error}</p>}
              <button
                type="submit"
                disabled={submitting || !email}
                className={`w-full py-3 rounded-full font-bold text-sm ${
                  submitting || !email ? 'bg-navy/40 text-cream/70 cursor-not-allowed' : 'bg-navy text-cream hover:bg-navy-dark'
                }`}
                data-testid="forgot-password-submit-btn"
              >
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-6" data-testid="forgot-password-sent">
            <p className="text-emerald-700 font-bold mb-2">If we have an account for that email,</p>
            <p className="text-navy/70 text-sm">we just sent a reset link there. Check your inbox — the link expires in 30 minutes.</p>
            <p className="text-navy/60 text-xs mt-4">For security, we always return the same message whether or not the email is registered.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
